import { useLayoutEffect, type RefObject } from 'react';

type FocusTarget = {
  ref: RefObject<HTMLElement | null>;
  enabled?: boolean;
};

interface UseExportAccessibilityWorkflowArgs {
  headerRef: RefObject<HTMLElement | null>;
  systemTableRef: RefObject<HTMLElement | null>;
  columnPanelRef: RefObject<HTMLElement | null>;
  exportActionsRef: RefObject<HTMLElement | null>;
  columnPanelOpen: boolean;
  settingsOpen: boolean;
  pathDialogOpen: boolean;
  guideOpen: boolean;
  tourOpen: boolean;
  onCloseColumnPanel: () => void;
  onCloseSettings: () => void;
  onClosePathDialog: () => void;
  onCloseGuide: () => void;
  onCloseTour: () => void;
}

const focusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusTarget(target: HTMLElement): void {
  const focusable = target.querySelector<HTMLElement>(focusableSelector);
  if (focusable) {
    focusable.focus();
    return;
  }

  target.focus();
}

function getEnabledTargets(targets: FocusTarget[]): HTMLElement[] {
  return targets
    .filter((target) => target.enabled !== false)
    .map((target) => target.ref.current)
    .filter((target): target is HTMLElement => Boolean(target));
}

export function useExportAccessibilityWorkflow({
  headerRef,
  systemTableRef,
  columnPanelRef,
  exportActionsRef,
  columnPanelOpen,
  settingsOpen,
  pathDialogOpen,
  guideOpen,
  tourOpen,
  onCloseColumnPanel,
  onCloseSettings,
  onClosePathDialog,
  onCloseGuide,
  onCloseTour,
}: UseExportAccessibilityWorkflowArgs): void {
  useLayoutEffect(() => {
    const handledF6Events = new WeakSet<KeyboardEvent>();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F6') {
        if (handledF6Events.has(event)) {
          return;
        }

        handledF6Events.add(event);
        const targets = getEnabledTargets([
          { ref: headerRef },
          { ref: systemTableRef },
          { ref: columnPanelRef, enabled: columnPanelOpen },
          { ref: exportActionsRef },
        ]);

        if (targets.length === 0) {
          return;
        }

        event.preventDefault();
        const activeElement = document.activeElement;
        const currentIndex = targets.findIndex((target) =>
          activeElement instanceof Node && target.contains(activeElement)
        );
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex =
          currentIndex === -1
            ? (event.shiftKey ? targets.length - 1 : 0)
            : (currentIndex + direction + targets.length) % targets.length;

        focusTarget(targets[nextIndex]);
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      if (event.key !== 'Escape') {
        return;
      }

      if (pathDialogOpen) {
        event.preventDefault();
        onClosePathDialog();
        return;
      }

      if (settingsOpen) {
        event.preventDefault();
        onCloseSettings();
        return;
      }

      if (guideOpen) {
        event.preventDefault();
        onCloseGuide();
        return;
      }

      if (tourOpen) {
        event.preventDefault();
        onCloseTour();
        return;
      }

      if (columnPanelOpen) {
        event.preventDefault();
        onCloseColumnPanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    columnPanelOpen,
    columnPanelRef,
    exportActionsRef,
    guideOpen,
    headerRef,
    onCloseColumnPanel,
    onCloseGuide,
    onClosePathDialog,
    onCloseSettings,
    onCloseTour,
    pathDialogOpen,
    settingsOpen,
    systemTableRef,
    tourOpen,
  ]);
}
